import type { TransparentConfigurationSnapshot } from "./types";

export type TransparentCustomerDetailInput = {
  salespersonNotes?: string | null;
  sourceComponent?: string | null;
  loadComponent?: string | null;
};

export function trimTransparentStaffText(value: string | null | undefined): string {
  return String(value ?? "").replace(/^\s+|\s+$/g, "");
}

export function formatTransparentCustomerDetailLines(details: {
  requiresEquipmentMatching?: boolean | null;
  sourceComponent?: string | null;
  loadComponent?: string | null;
  salespersonNotes?: string | null;
}): string[] {
  const lines: string[] = [];
  if (details.requiresEquipmentMatching) {
    const source = trimTransparentStaffText(details.sourceComponent);
    const load = trimTransparentStaffText(details.loadComponent);
    if (source) lines.push(`Source: ${source}`);
    if (load) lines.push(`Load: ${load}`);
  }
  const notes = trimTransparentStaffText(details.salespersonNotes);
  if (notes) {
    const [first, ...rest] = notes.split("\n");
    lines.push(`Notes: ${first ?? ""}`);
    for (const line of rest) lines.push(line);
  }
  return lines;
}

export function validateTransparentEquipmentMatching(
  requiresEquipmentMatching: boolean,
  sourceComponent: string | null | undefined,
  loadComponent: string | null | undefined,
): string | null {
  if (!requiresEquipmentMatching) return null;
  if (!trimTransparentStaffText(sourceComponent) || !trimTransparentStaffText(loadComponent)) {
    return "Source component and Load component are required for interconnects and speaker cables at Reference level and above.";
  }
  return null;
}

/**
 * Attach optional notes and equipment-matching fields without changing price, SKU or image identity.
 * Qualifying Source/Load values are excluded when the resolved family does not require matching.
 */
export function attachTransparentCustomerDetails(
  snapshot: TransparentConfigurationSnapshot,
  details: TransparentCustomerDetailInput,
): TransparentConfigurationSnapshot {
  const requiresEquipmentMatching = Boolean(snapshot.requiresEquipmentMatching);
  const sourceComponent = requiresEquipmentMatching ? trimTransparentStaffText(details.sourceComponent) || null : null;
  const loadComponent = requiresEquipmentMatching ? trimTransparentStaffText(details.loadComponent) || null : null;
  const salespersonNotes = trimTransparentStaffText(details.salespersonNotes) || null;
  const appendix = formatTransparentCustomerDetailLines({
    requiresEquipmentMatching,
    sourceComponent,
    loadComponent,
    salespersonNotes,
  }).join("\n");
  const customerDescription = appendix
    ? `${snapshot.customerDescription}\n\n${snapshot.customerDisplayName}\n\n${appendix}`
    : snapshot.customerDescription;
  const internalOrderDescription = appendix
    ? `${snapshot.internalOrderDescription}\n${appendix}`
    : snapshot.internalOrderDescription;
  return {
    ...snapshot,
    salespersonNotes,
    sourceComponent,
    loadComponent,
    customerDescription,
    internalOrderDescription,
  };
}

export function transparentCustomerFacingAppendix(snapshot: Pick<
  TransparentConfigurationSnapshot,
  "requiresEquipmentMatching" | "sourceComponent" | "loadComponent" | "salespersonNotes"
> | null | undefined): string {
  return formatTransparentCustomerDetailLines({
    requiresEquipmentMatching: snapshot?.requiresEquipmentMatching,
    sourceComponent: snapshot?.sourceComponent,
    loadComponent: snapshot?.loadComponent,
    salespersonNotes: snapshot?.salespersonNotes,
  }).join("\n");
}
